import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { formatCurrency } from '../../lib/formatters';

const SortableHeader = ({ children, column, currentSort, onSort, className = '' }) => (
  <TableHead
    className={`cursor-pointer hover:bg-gray-200 text-right ${className}`}
    onClick={() => onSort(column)}
  >
    {children} {currentSort.column === column && (currentSort.direction === 'asc' ? '↑' : '↓')}
  </TableHead>
);

const SyndicatorsTable = ({
  syndicators,
  sortConfig,
  onSort,
  onSelectSyndicator,
  onWithdraw,
  isAdmin = false
}) => {
  const [selectedIds, setSelectedIds] = useState([]);

  const handleCheckboxChange = (syndicatorId, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, syndicatorId]);
    } else {
      setSelectedIds(prev => prev.filter(id => id !== syndicatorId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const eligibleIds = syndicators
        .filter(s => (s.total_balance || 0) > 0)
        .map(s => s.syndicator_id);
      setSelectedIds(eligibleIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkWithdraw = () => {
    if (selectedIds.length === 0) return;
    // Handle bulk withdrawal
    if (onWithdraw) {
      selectedIds.forEach(id => {
        const syndicator = syndicators.find(s => s.syndicator_id === id);
        if (syndicator && syndicator.total_balance > 0) {
          onWithdraw(syndicator);
        }
      });
    }
  };

  // Calculate totals
  const totals = syndicators.reduce((acc, syn) => ({
    advance: acc.advance + (syn.total_advance || 0),
    target: acc.target + (syn.total_target || 0),
    collected: acc.collected + (syn.total_collected || 0),
    withdrawals: acc.withdrawals + (syn.total_withdrawals || 0),
    balance: acc.balance + (syn.total_balance || 0),
  }), { advance: 0, target: 0, collected: 0, withdrawals: 0, balance: 0 });

  const eligibleForWithdraw = syndicators.filter(s => (s.total_balance || 0) > 0);
  const allSelected = eligibleForWithdraw.length > 0 && 
    eligibleForWithdraw.every(s => selectedIds.includes(s.syndicator_id));

  return (
    <div className="space-y-4">
      {/* Bulk Actions Header */}
      {isAdmin && selectedIds.length > 0 && (
        <div className="flex justify-end">
          <Button 
            onClick={handleBulkWithdraw}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Withdraw (up to {Math.min(selectedIds.length, 15)})
          </Button>
        </div>
      )}

      <div className="overflow-x-auto border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              {isAdmin && (
                <TableHead className="w-12">
                  <Checkbox 
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-syndicators"
                  />
                </TableHead>
              )}
              <SortableHeader 
                column="syndicator_name" 
                currentSort={sortConfig} 
                onSort={onSort}
                className="text-left"
              >
                Investor
              </SortableHeader>
              <SortableHeader column="total_advance" currentSort={sortConfig} onSort={onSort}>
                Advance Amount
              </SortableHeader>
              <SortableHeader column="total_target" currentSort={sortConfig} onSort={onSort}>
                Target Collection
              </SortableHeader>
              <SortableHeader column="total_collected" currentSort={sortConfig} onSort={onSort}>
                Running Collection
              </SortableHeader>
              <SortableHeader column="total_withdrawals" currentSort={sortConfig} onSort={onSort}>
                Withdrawals
              </SortableHeader>
              <SortableHeader column="total_balance" currentSort={sortConfig} onSort={onSort}>
                Balance
              </SortableHeader>
              {isAdmin && <TableHead className="text-center">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {syndicators.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 6} className="text-center py-8 text-gray-500">
                  No syndicators found
                </TableCell>
              </TableRow>
            ) : (
              <>
                {syndicators.map((syn) => {
                  const hasBalance = (syn.total_balance || 0) > 0;
                  const isSelected = selectedIds.includes(syn.syndicator_id);
                  
                  return (
                    <TableRow
                      key={syn.syndicator_id}
                      className={`hover:bg-blue-50 ${isSelected ? 'bg-blue-50' : ''}`}
                      data-testid={`syndicator-row-${syn.syndicator_id}`}
                    >
                      {isAdmin && (
                        <TableCell>
                          <Checkbox 
                            checked={isSelected}
                            onCheckedChange={(checked) => handleCheckboxChange(syn.syndicator_id, checked)}
                            disabled={!hasBalance}
                            data-testid={`checkbox-${syn.syndicator_id}`}
                          />
                        </TableCell>
                      )}
                      <TableCell 
                        className="font-medium text-blue-600 cursor-pointer"
                        onClick={() => onSelectSyndicator(syn)}
                      >
                        {syn.syndicator_name}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(syn.total_advance)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(syn.total_target)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(syn.total_collected)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(syn.total_withdrawals)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(syn.total_balance)}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-center">
                          {hasBalance ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onWithdraw) onWithdraw(syn);
                              }}
                              className="text-blue-600 border-blue-600 hover:bg-blue-50"
                              data-testid={`withdraw-btn-${syn.syndicator_id}`}
                            >
                              Withdraw
                            </Button>
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                
                {/* Totals Row */}
                <TableRow className="bg-gray-100 font-semibold border-t-2">
                  {isAdmin && <TableCell></TableCell>}
                  <TableCell className="text-left">
                    Total ({syndicators.length} investors)
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(totals.advance)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(totals.target)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(totals.collected)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(totals.withdrawals)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(totals.balance)}
                  </TableCell>
                  {isAdmin && <TableCell></TableCell>}
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SyndicatorsTable;
